import { Form, Formik } from 'formik';

import Button from '@/components/common/Button/Button';
import { Fields, validationSchema } from './validationSchema';
// eslint-disable-next-line import/no-named-as-default
import ValidationAlert from '@/components/common/ValidationAlert/ValidationAlert';
import { useModalWindowApi } from '@/components/ModalWindowApi/useModalWindowApi';
import { NUMBER_OF_MONTHS_IN_YEAR } from '@/config/common.config';
import ModalConfirmation from '../ModalConfirmation/ModalConfirmation';
import Input from '@/components/common/Input/Input';
import styles from './custom-billing-interval.module.scss';

interface ICustomBillingIntervalProps {
  applyChanges: (selectedOption: BillingInterval) => void;
  withConfirmation?: boolean;
}

export default function CustomBillingInterval({ applyChanges, withConfirmation = true }: ICustomBillingIntervalProps) {
  const { addModal, clearAllModal } = useModalWindowApi();

  const onSubmit = (values: Fields) => {
    const interval = parseInt(values.interval as string, 10);

    const intervalObject: BillingInterval = {
      __typename: 'BillingIntervalType',
      billingInterval: `${interval}Years`,
      formattedBillingInterval: `${interval} Years`,
      monthsCount: interval * NUMBER_OF_MONTHS_IN_YEAR,
      enabled: true,
      showable: false,
      contractDBRLimit: {
        maxDays: 0,
        minDays: 0
      },
      globalDBRLimit: {
        maxDays: 0,
        minDays: 0
      },
    };

    if (withConfirmation) {
      addModal({
        modalType: 'confirmation',
        title: 'Please confirm you wish to continue',
        ModalContent: () => <ModalConfirmation fieldName="billing interval" />,
        onCancelButtonParams: {},
        onConfirmButtonParams: {
          callback: () => {
            applyChanges(intervalObject);
            clearAllModal();
          }
        }
      });
    }
    else {
      applyChanges(intervalObject);
      clearAllModal();
    }
  };

  return (
    <Formik<Fields>
      initialValues={{ interval: '' }}
      validationSchema={validationSchema}
      validateOnMount={true}
      onSubmit={onSubmit}
    >
      {({ isValid, errors, values, touched, setFieldValue, setFieldTouched }) => (
        <Form>
          <label
            className={styles['years-label']}
            htmlFor="interval-years"
          >
            Interval (years)
          </label>
          <div className="d-flex justify-content-between">
            <div>
              <Input
                autoFocus={true}
                id="interval-years"
                name="interval"
                type="number"
                className={styles['years-input']}
                step={1}
                min={2}
                max={10}
                placeholder="2 - 10 years"
                value={values.interval}
                onChange={(e) => {
                  setFieldTouched('interval', true, false);
                  setFieldValue('interval', e.target.value);
                }}
              />

              {errors.interval && touched.interval && (
                <ValidationAlert isFloating={false}>
                  {errors.interval}
                </ValidationAlert>
              )}
            </div>

            <Button
              type="submit"
              disabled={!isValid}
            >
              Add
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
